import { useQuery, useMutation } from '@apollo/client';
import styled from '@emotion/styled';
import {
  Segment,
  Form,
  Row,
  Column,
  FormTitle,
  Button,
  FormDescription,
  device,
  useMedia,
  theme,
  typography,
} from '@landsbankinn/ui';
import { LandsbankinnContext } from '@landsbankinn/utils';
import groupBy from 'lodash.groupby';
import Link from 'next/link';
import React, { useContext, useState } from 'react';
import type { OrderGiftCardInput } from '../../../../__generated__/globalTypes';
import { Region } from '../../../../__generated__/globalTypes';
import { useRegionOptions } from '../../../components/service-points/filters/RegionDropdown';
import type { GiftCardDeliveryPlaces } from '../../../graphql/giftCard/__generated__/GiftCardDeliveryPlaces';
import type {
  OrderGiftCard as IOrderGiftCard,
  OrderGiftCardVariables,
} from '../../../graphql/giftCard/__generated__/OrderGiftCard';
import { GIFT_CARD_DELIVERY_PLACES } from '../../../graphql/giftCard/giftCardDeliveryPlaces';

import { ORDER_GIFT_CARD } from '../../../graphql/giftCard/orderGiftCard';
import { CorporateForm } from './Corporate';
import { PersonForm } from './Person';
import { Success } from './Success';

interface IProps {
  type: 'company' | 'person';
}
const A = styled.a`
  text-decoration: underline;
`;
const BlueContainer = styled.div`
  padding-top: ${(props) => props.theme.baseline * 5}px;
  background-color: ${(props) => props.theme.shades.primary2};
  & > section {
    background-color: ${(props) => props.theme.shades.primary2};
  }
  @media ${device.tablet} {
    padding-bottom: ${(props) => props.theme.baseline * 26}px;
    padding-top: ${(props) => props.theme.baseline * 18}px;
  }
`;

const TitleRow = styled(Row)`
  @media ${device.desktop} {
    margin-bottom: 36px;
  }
`;

const StyledButton = styled(Button)`
  width: 100%;
`;

const Host: React.FC = ({ children }) => {
  const isMobile = useMedia('sm');

  if (!isMobile) {
    return (
      <Segment>
        <BlueContainer>{children}</BlueContainer>
      </Segment>
    );
  }
  return (
    <BlueContainer>
      <Segment>{children}</Segment>
    </BlueContainer>
  );
};

const Text = styled.p`
  ${typography.bodySmall}
  margin-bottom: 16px;
  @media ${device.tablet} {
    margin-bottom: 0px;
  }
`;

const FormContainer = styled.div`
  margin-bottom: ${(props) => props.theme.baseline * 8}px;
`;

const initialFormData = {
  numberOfGiftCards: 1,
  amountOnEachCard: 0,
  name: '',
  kennitala: '',
  phoneNumber: '',
  email: '',
  servicePointId: '',
  description: null,
  employeeName: null,
  withdrawalAccountNumber: null,
};

export function OrderGiftCard({ type }: IProps) {
  const [orderGiftCardInput, setOrderGiftCardInput] =
    useState<OrderGiftCardInput>(initialFormData);

  const { client } = useContext(LandsbankinnContext);
  const { options } = useRegionOptions();

  const [step, setStep] = useState<'form' | 'success'>('form');
  const [kennitalaNotFound, setKennitalaNotFound] = useState(false);

  const [deliveryPlacesOptions, setDeliveryPlacesOptions] = useState<
    { label: string; options: { label: string; value: string }[] }[] | undefined
  >(undefined);

  const [orderGiftCard, { loading }] = useMutation<
    IOrderGiftCard,
    OrderGiftCardVariables
  >(ORDER_GIFT_CARD, {
    client,
    onCompleted: (data) => {
      if (data.orderGiftCard.__typename === 'CrmCasePayload') {
        setStep('success');
        setOrderGiftCardInput(initialFormData);
      }
      if (data.orderGiftCard.__typename === 'KennitalaNotFoundError') {
        setKennitalaNotFound(true);
      }
    },
  });

  useQuery<GiftCardDeliveryPlaces>(GIFT_CARD_DELIVERY_PLACES, {
    client,
    onCompleted: (data) => {
      const branches = data.servicePoints.edges.filter((edge) => {
        if (type === 'person') {
          return (
            edge.node.id !==
            'U2VydmljZVBvaW50OjNiMzVkNWNiLWQzYmUtZWIxMS04MGY0LTAwNTA1NmJlMGNjZA=='
          ); // fyrirtækamiðstöð
        }
        return true;
      });
      const groupedBrances = groupBy(branches, (b) => b.node.region);
      const places = Object.keys(groupedBrances).map(function (key) {
        return {
          label: options.find((op) => op.value === Region[key])?.label ?? '',
          options: groupedBrances[key].map((item) => {
            if (item.node.branches === null) {
              return {
                label: '',
                value: '',
              };
            }
            return {
              label: item.node.branches[0].name,
              value: item.node.branches[0].id,
            };
          }),
        };
      });

      setDeliveryPlacesOptions(places);
    },
  });

  return (
    <Host>
      {step === 'form' && (
        <Form
          onSubmit={() =>
            orderGiftCard({ variables: { data: orderGiftCardInput } })
          }
        >
          <TitleRow>
            <Column
              sm={{ width: 12 / 12 }}
              md={{ width: 8 / 12, offset: 1 / 12 }}
            >
              <FormTitle text="Panta gjafakort" />
              <FormDescription>
                Þú getur pantað gjafakort og sótt í næsta útibú. Þú getur líka
                nýtt þér gjafakortasjálfsalana í Mjódd og Vesturbæ sem eru opnir
                allan sólarhringinn.
                {type !== 'company' && (
                  <>
                    <br />
                    Magnpantanir eru gerðar á{' '}
                    <Link
                      href="/fyrirtaeki/kort-og-greidslur/gjafakort"
                      passHref
                    >
                      <A>gjafakortasíðu fyrirtækja</A>
                    </Link>
                  </>
                )}
              </FormDescription>
            </Column>
          </TitleRow>
          <FormContainer>
            {type === 'company' ? (
              <CorporateForm
                setKennitalaNotFound={setKennitalaNotFound}
                kennitalaNotFound={kennitalaNotFound}
                setOrderGiftCardInput={setOrderGiftCardInput}
                deliveryPlaceOptions={deliveryPlacesOptions}
                formData={orderGiftCardInput}
              />
            ) : (
              <PersonForm
                setKennitalaNotFound={setKennitalaNotFound}
                kennitalaNotFound={kennitalaNotFound}
                setOrderGiftCardInput={setOrderGiftCardInput}
                deliveryPlaceOptions={deliveryPlacesOptions}
                formData={orderGiftCardInput}
              />
            )}
          </FormContainer>
          <Row>
            <Column
              md={{ width: 5 / 12, offset: 1 / 12 }}
              sm={{ width: 12 / 12 }}
              align="center"
            >
              <Text>
                Ég samþykki með pöntuninni að framangreindar upplýsingar séu
                unnar í samræmi við persónuverndarstefnu bankans.
              </Text>
            </Column>

            <Column md={{ width: 5 / 12 }} sm={{ width: 12 / 12 }}>
              <StyledButton
                bg={theme.colors.primary}
                title="Panta gjafakort"
                loading={loading}
              />
            </Column>
          </Row>
        </Form>
      )}
      {step === 'success' && <Success setStep={setStep} />}
    </Host>
  );
}
